import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 2, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50" data-testid="privacy-page">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-28 pb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1C3A7A] mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last Updated: {lastUpdated}</p>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">1. Introduction</h2>
            <p>
              At RecoveryMatch ("we," "our," or "us"), we take your privacy seriously. This Privacy Policy 
              explains how we collect, use, disclose, and protect your personal information when you use 
              our mobile application and website (the "Service").
            </p>
            <p className="mt-3">
              We understand that our users share sensitive information about their recovery journey. We are 
              committed to protecting this information with the utmost care and transparency.
            </p>
            <p className="mt-3 font-semibold text-[#1C3A7A]">
              By using RecoveryMatch, you consent to the data practices described in this Privacy Policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.1 Information You Provide</h3>
            <p>When you create an account and use RecoveryMatch, you may provide:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, date of birth, gender, and sexual orientation.</li>
              <li><strong>Profile Information:</strong> Photos, bio, location, and preferences for potential matches.</li>
              <li><strong>Recovery Information:</strong> Sobriety/clean date, recovery programs you participate in (e.g., AA, NA), and meeting preferences. <em>This information is optional but helps us connect you with compatible matches.</em></li>
              <li><strong>Communications:</strong> Messages you send to other users through our chat feature.</li>
              <li><strong>Payment Information:</strong> When purchasing subscriptions or coins, your payment details are processed by our third-party payment processors (we do not store full credit card numbers).</li>
              <li><strong>Support Requests:</strong> Information you provide when contacting customer support.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">2.2 Information Collected Automatically</h3>
            <p>When you use our Service, we automatically collect:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, and mobile network information.</li>
              <li><strong>Usage Data:</strong> Features you use, actions you take (swipes, matches, messages), time spent on the app, and interaction patterns.</li>
              <li><strong>Location Data:</strong> With your permission, we collect your approximate location to show you nearby matches. You can disable location services in your device settings.</li>
              <li><strong>Log Data:</strong> IP address, browser type, access times, and pages viewed.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">2.3 Information from Third Parties</h3>
            <p>We may receive information from:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Social media platforms if you choose to link your account or sign up using social login.</li>
              <li>Analytics providers who help us understand app usage.</li>
              <li>Payment processors for transaction verification.</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Provide the Service:</strong> Create your profile, show you potential matches, facilitate messaging, and process transactions.</li>
              <li><strong>Personalize Your Experience:</strong> Recommend compatible matches based on your preferences and recovery journey.</li>
              <li><strong>Improve Our Service:</strong> Analyze usage patterns, troubleshoot issues, and develop new features.</li>
              <li><strong>Communicate With You:</strong> Send notifications about matches, messages, and important updates. You can manage notification preferences in settings.</li>
              <li><strong>Ensure Safety:</strong> Detect and prevent fraud, abuse, and violations of our Terms of Service.</li>
              <li><strong>Legal Compliance:</strong> Comply with applicable laws and respond to legal requests.</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">4. How We Share Your Information</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.1 With Other Users</h3>
            <p>
              Your profile information (name, photos, bio, age, location, recovery milestones) is visible to 
              other RecoveryMatch users. Messages you send are visible to their recipients. You control what 
              recovery information you share on your profile.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">4.2 With Service Providers</h3>
            <p>We share information with third-party vendors who help us operate the Service:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Cloud Hosting:</strong> Our servers are hosted by secure cloud providers.</li>
              <li><strong>Payment Processing:</strong> Subscription and purchase payments are processed by third-party payment processors.</li>
              <li><strong>Analytics:</strong> We use analytics services to understand how users interact with our app.</li>
              <li><strong>Push Notifications:</strong> We use notification services to deliver messages and alerts.</li>
            </ul>
            <p className="mt-3">
              These providers are contractually obligated to protect your information and only use it for 
              the purposes we specify.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">4.3 For Legal Reasons</h3>
            <p>We may disclose your information if required to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Comply with a legal obligation, court order, or government request.</li>
              <li>Protect the safety of any person or the public.</li>
              <li>Protect against legal liability or fraud.</li>
              <li>Enforce our Terms of Service.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">4.4 Business Transfers</h3>
            <p>
              If RecoveryMatch is involved in a merger, acquisition, or sale of assets, your information may 
              be transferred as part of that transaction. We will notify you of any change in ownership or 
              use of your personal information.
            </p>

            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-semibold text-green-800">What We Don't Do:</p>
              <ul className="list-disc pl-6 mt-2 text-green-700">
                <li>We do NOT sell your personal information to third parties.</li>
                <li>We do NOT share your recovery information with advertisers.</li>
                <li>We do NOT share your data with employers or insurance companies.</li>
              </ul>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">5. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to 
              provide you the Service. After you delete your account:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Your profile is immediately hidden from other users.</li>
              <li>Most personal data is deleted within 30 days.</li>
              <li>Some information may be retained longer for legal compliance, fraud prevention, or to resolve disputes.</li>
              <li>Anonymized, aggregated data may be retained indefinitely for analytics.</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">6. Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Access Your Data:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Correct Your Data:</strong> Update or correct inaccurate information in your profile settings.</li>
              <li><strong>Delete Your Data:</strong> Delete your account and request removal of your personal information.</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format.</li>
              <li><strong>Opt-Out of Marketing:</strong> Unsubscribe from promotional emails at any time.</li>
              <li><strong>Manage Permissions:</strong> Control location access, notifications, and other permissions in your device settings.</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, go to Settings {">"} Privacy in the app, or contact us at support@recoverymatch.com.
            </p>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="font-semibold text-blue-800">For California Residents (CCPA):</p>
              <p className="text-blue-700 mt-1">
                You have additional rights under the California Consumer Privacy Act, including the right 
                to know what personal information we collect and the right to request deletion. We do not 
                sell personal information as defined by the CCPA.
              </p>
            </div>

            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="font-semibold text-purple-800">For EU/UK Residents (GDPR):</p>
              <p className="text-purple-700 mt-1">
                You have rights under the General Data Protection Regulation, including the right to access, 
                rectify, erase, restrict processing, and data portability. You may also lodge a complaint 
                with your local data protection authority.
              </p>
            </div>
          </section>

          {/* Account Deletion */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">7. Account Deletion</h2>
            <p>You can delete your RecoveryMatch account at any time:</p>
            <ol className="list-decimal pl-6 mt-3 space-y-2">
              <li>Open the RecoveryMatch app</li>
              <li>Go to Profile {">"} Settings</li>
              <li>Tap "Delete Account"</li>
              <li>Confirm your decision</li>
            </ol>
            <p className="mt-3">
              <strong>Can't access the app?</strong> You can also request deletion online at{" "}
              <a href="/delete-account" className="text-[#1C3A7A] underline hover:text-[#FAB710]">
                recoverymatch.com/delete-account
              </a>
            </p>
            <p className="mt-3">
              Upon deletion, your profile will be immediately removed and your data will be permanently 
              deleted within 30 days. Note that some information shared with other users 
              (e.g., messages) may remain visible to them.
            </p>
            <p className="mt-3">
              If you need assistance, contact{" "}
              <a href="mailto:support@recoverymatch.com" className="text-[#1C3A7A] underline">
                support@recoverymatch.com
              </a>
            </p>
          </section>

          {/* Security */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">8. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Encryption of data in transit (TLS/SSL) and at rest.</li>
              <li>Secure password hashing.</li>
              <li>Regular security audits and vulnerability assessments.</li>
              <li>Access controls limiting employee access to user data.</li>
              <li>Monitoring for suspicious activity.</li>
            </ul>
            <p className="mt-3">
              While we strive to protect your information, no method of transmission over the Internet is 
              100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Children */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">9. Children's Privacy</h2>
            <p>
              RecoveryMatch is not intended for anyone under the age of 18. We do not knowingly collect 
              personal information from children. If we discover that a child under 18 has created an 
              account, we will immediately delete it and all associated data.
            </p>
            <p className="mt-3">
              If you believe a child has provided us with personal information, please contact us at 
              support@recoverymatch.com.
            </p>
          </section>

          {/* International */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">10. International Data Transfers</h2>
            <p>
              RecoveryMatch is based in the United States. If you access our Service from outside the US, 
              your information may be transferred to, stored, and processed in the US or other countries 
              where our service providers operate.
            </p>
            <p className="mt-3">
              By using RecoveryMatch, you consent to the transfer of your information to countries that may 
              have different data protection laws than your country of residence.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Posting the updated policy on our website and app.</li>
              <li>Updating the "Last Updated" date at the top of this page.</li>
              <li>Sending you an email or in-app notification for significant changes.</li>
            </ul>
            <p className="mt-3">
              Your continued use of RecoveryMatch after changes are posted constitutes acceptance of the 
              updated Privacy Policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">12. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data 
              practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p><strong>Email:</strong> support@recoverymatch.com</p>
              <p><strong>Support:</strong> support@recoverymatch.com</p>
              <p><strong>Address:</strong> RecoveryMatch Inc., [Your Address]</p>
            </div>
            <p className="mt-4">
              We will respond to your inquiry within 30 days.
            </p>
          </section>

        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <button 
            onClick={() => navigate("/")}
            className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition"
          >
            ← Back to Home
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
